/* import __COLOCATED_TEMPLATE__ from './task-review.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type SampleApiDoc } from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/suggestions/suggestion-accordion-items/suggestion-sample-api-item';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';
import { TaskSuggestionStatus } from 'embercom/models/content-service/task-suggestion';
import type { OptimizeSuggestionItem } from 'embercom/components/reporting/automation/ai-insights/optimize-suggestions/lib/constants';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import type RouterService from '@ember/routing/router-service';
import type AppService from 'embercom/services/app-service';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type Store from '@ember-data/store';

interface Signature {
  Args: {
    taskSuggestion: OptimizeSuggestionItem;
    onConversationClicked: (conversation: ConversationExploreConversation) => void;
    suggestedConversationIds: string[];
    impactRanking?: number;
    conversationCount?: number;
  };
}

export default class TaskReview extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare appService: AppService;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare store: Store;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;

  @tracked openSectionId: string | null = '1';
  @tracked showSidePanel = true;
  @tracked openSidebarSection: string | null = 'context';
  @tracked isUpdating = false;

  get taskSuggestion(): TaskSuggestion {
    return this.args.taskSuggestion.suggestion as TaskSuggestion;
  }

  get implementationEffortScore(): number {
    return this.taskSuggestion.implementationDifficultyScore || -1;
  }

  get topicsString(): string {
    if (!this.args.taskSuggestion.topicList || this.args.taskSuggestion.topicList.length === 0) {
      return '';
    }
    return this.args.taskSuggestion.topicList.join(', ');
  }

  get buildSampleApiDoc(): SampleApiDoc {
    return {
      requestMethod: this.taskSuggestion.actionMethod,
      requestUrl: this.taskSuggestion.actionPath,
      requestBody: this.taskSuggestion.actionParams,
      responseBody: this.taskSuggestion.mockResponse,
    } as SampleApiDoc;
  }

  @action
  onOpenSectionChange(sectionId: string): void {
    this.openSectionId = sectionId;
  }

  @action
  toggleSidePanel(): void {
    this.showSidePanel = !this.showSidePanel;
  }

  @action
  toggleSidebarSection(sectionId: string): void {
    this.openSidebarSection = this.openSidebarSection === sectionId ? null : sectionId;
  }

  @action
  async openGuidance() {
    let appId = this.appService.app.id;

    let guidelineTitle = this.taskSuggestion.actionName;

    let newGuideline = await this.aiAgentGuidelineService.createNewGuideline('routing', {
      title: guidelineTitle,
      prompt: this.taskSuggestion.guidanceRawDescription || this.taskSuggestion.guidanceDescription,
    });

    // Check if component was destroyed during async operation
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    let url = this.router.urlFor('apps.app.automation.fin-ai-agent.escalation', appId, {
      queryParams: { openGuideline: `guideline:${newGuideline.id}` },
    });
    safeWindowOpen(url, '_blank');
  }

  @action
  async handleApprove() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('automation.analyze.task-suggestions.confirm-approve.title'),
      body: this.intl.t('automation.analyze.task-suggestions.confirm-approve.body'),
      confirmButtonText: this.intl.t('automation.analyze.task-suggestions.confirm-approve.confirm'),
      cancelButtonText: this.intl.t('automation.analyze.task-suggestions.confirm-approve.cancel'),
    });

    // Check if component was destroyed while waiting for confirmation
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    if (confirmed) {
      await this.updateTaskSuggestionStatus(TaskSuggestionStatus.Approved);
    }
  }

  @action
  async handleReject() {
    let confirmed = await this.intercomConfirmService.confirm({
      title: this.intl.t('automation.analyze.task-suggestions.confirm-reject.title'),
      body: this.intl.t('automation.analyze.task-suggestions.confirm-reject.body'),
      confirmButtonText: this.intl.t('automation.analyze.task-suggestions.confirm-reject.confirm'),
      cancelButtonText: this.intl.t('automation.analyze.task-suggestions.confirm-reject.cancel'),
    });

    // Check if component was destroyed while waiting for confirmation
    if (this.isDestroying || this.isDestroyed) {
      return;
    }

    if (confirmed) {
      await this.updateTaskSuggestionStatus(TaskSuggestionStatus.Rejected);
    }
  }

  private async updateTaskSuggestionStatus(newStatus: TaskSuggestionStatus) {
    this.isUpdating = true;

    let currentEntityId = this.args.taskSuggestion.entityId;
    let isApproved = newStatus === TaskSuggestionStatus.Approved;

    try {
      // Call the appropriate adapter method (approve or reject)
      // @ts-expect-error - adapterFor doesn't know about our custom adapter
      let adapter = this.store.adapterFor('content-service/task-suggestion');
      if (isApproved) {
        // @ts-expect-error - custom adapter method
        await adapter.approve(this.taskSuggestion);
      } else {
        // @ts-expect-error - custom adapter method
        await adapter.reject(this.taskSuggestion);
      }

      // Check if component was destroyed during async operation
      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      let nextSuggestion = this.optimizeSuggestionsService.getNextTaskSuggestion(currentEntityId);

      this.optimizeSuggestionsService.removeTaskSuggestionFromList(currentEntityId);

      if (nextSuggestion) {
        this.router.transitionTo('apps.app.automation.fin-ai-agent.analyze.optimize-suggestions', {
          queryParams: {
            activeContentId: nextSuggestion.entityId,
            activeContentType: nextSuggestion.contentType,
          },
        });
      } else {
        this.router.transitionTo('apps.app.automation.fin-ai-agent.analyze.optimize-suggestions', {
          queryParams: {
            activeContentId: null,
            activeContentType: null,
          },
        });
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          isApproved
            ? 'automation.analyze.task-suggestions.notification.approved'
            : 'automation.analyze.task-suggestions.notification.rejected',
        ),
      );
    } catch (error) {
      // Check if component was destroyed during async operation
      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      console.error('Error updating task suggestion:', error);
      this.notificationsService.notifyError(
        this.intl.t('automation.analyze.task-suggestions.notification.error'),
      );
    } finally {
      // Only update state if component is still alive
      if (!this.isDestroying && !this.isDestroyed) {
        this.isUpdating = false;
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::TaskReview': typeof TaskReview;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/task-review': typeof TaskReview;
  }
}
